import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { useClientStore } from '@/client/stores/client';
import { withErrorHandling } from '@/client/utils/errors';
import { DEFAULT_GC_TIME, DEFAULT_STALE_TIME } from '@/client/constants';
import type { Currency } from '@/client/types/currency';

const BASE = '/api/admin/currencies';

export const currencyKeys = {
  all: ['admin', 'currencies'] as const,
  list: () => [...currencyKeys.all, 'list'] as const,
  detail: (id: number) => [...currencyKeys.all, 'detail', id] as const,
};

/** ff-api wraps payloads as `{ status, data }`. */
const unwrapEnvelope = (raw: unknown): any =>
  raw != null &&
  typeof raw === 'object' &&
  'status' in (raw as object) &&
  'data' in (raw as object)
    ? (raw as { data: unknown }).data
    : raw;

const normalizeList = (raw: unknown): Currency[] => {
  const payload = unwrapEnvelope(raw);
  if (Array.isArray(payload)) return payload as Currency[];
  if (Array.isArray(payload?.currencies)) return payload.currencies as Currency[];
  if (Array.isArray(payload?.items)) return payload.items as Currency[];
  return [];
};

const normalizeOne = (raw: unknown): Currency | null => {
  const payload = unwrapEnvelope(raw);
  if (payload == null || typeof payload !== 'object') return null;
  const obj = (payload as any)?.currency ?? payload;
  if (obj == null || typeof obj !== 'object') return null;
  return obj as Currency;
};

export const useCurrenciesQuery = (enabled = true) => {
  const request = useClientStore((s) => s.request);
  return useQuery({
    queryKey: currencyKeys.list(),
    queryFn: withErrorHandling(async (): Promise<Currency[]> => {
      const raw = await request<unknown>(BASE);
      return normalizeList(raw);
    }, 'Failed to fetch currencies'),
    enabled,
    staleTime: DEFAULT_STALE_TIME,
    gcTime: DEFAULT_GC_TIME,
    placeholderData: keepPreviousData,
  });
};

export const useCurrencyQuery = (id: number | null, enabled = true) => {
  const request = useClientStore((s) => s.request);
  return useQuery({
    queryKey: currencyKeys.detail(id ?? 0),
    queryFn: withErrorHandling(async (): Promise<Currency | null> => {
      const raw = await request<unknown>(`${BASE}/${id}`);
      return normalizeOne(raw);
    }, 'Failed to fetch currency'),
    enabled: enabled && typeof id === 'number' && id > 0,
    staleTime: DEFAULT_STALE_TIME,
    gcTime: DEFAULT_GC_TIME,
  });
};
